import { useCallback } from 'react';
import { type DefaultError, useMutation } from '@tanstack/react-query';
import { type SupportedLocale } from '@zep-us/i18n';
import { nanoid } from 'nanoid';

import { logger } from '@zep/core-logger';

import { AI_HTTP_CLIENT } from '@/api/ai/client';
import { useLocale } from '@/hooks';
import { useBuildFormContext } from '@/views/build/form/schema';
import { useShowAiErrorAlert } from '@/views/build/hook';

import { useQuizFormField } from '../../hook/useQuizFormField';
import { useQuizControl } from '../../QuizControlProvider';

import {
  useAiGenerateSubjectiveStatusValue,
  useSetAiGenerateSubjectiveStatus,
} from './useAiGeneateSubjectiveStatus';

export interface AnswerItem {
  key: number;
  text: string;
}

export interface AiGenerateSubjectiveAnswerListQuery {
  subject?: string;
  schoolType?: string;
  grade?: string;
  question: string;
  answerList: Omit<AnswerItem, 'text'>[];
  previousOutput?: AnswerItem[];
  locale?: SupportedLocale;
}

export const useAiGenerateAnswerList = () => {
  const [answerList] = useQuizFormField({
    field: 'subjectiveData.answerList',
  });

  const [question] = useQuizFormField({
    field: 'question',
  });

  const { id } = useQuizControl();
  const { setValue, getValues } = useBuildFormContext();

  const { generatingIndexes } = useAiGenerateSubjectiveStatusValue();
  const { setIsGeneratingBulk } = useSetAiGenerateSubjectiveStatus();

  const { mutateAsync, isPending } =
    useAiGenerateSubjectiveAnswerListMutation();
  const disabled = isPending || !question || generatingIndexes.length > 0;
  const { locale } = useLocale();
  const showAiErrorAlert = useShowAiErrorAlert();

  const handleGeneratedAnswerList = useCallback(
    (generatedAnswerList: AnswerItem[]) => {
      const quizList = getValues(`quizList`);
      const targetQuizIndex = quizList.findIndex(quiz => quiz.id === id);
      if (targetQuizIndex === -1) {
        console.error(`target quiz not found: ${id}`);
        return;
      }

      setValue(
        `quizList.${targetQuizIndex}.subjectiveData.answerList`,
        generatedAnswerList.map(a => ({
          key: nanoid(8),
          text: a.text,
        })),
      );
    },
    [setValue, id, getValues],
  );

  const generate = useCallback(async () => {
    const start = Date.now();
    logger.track('add_subjective_answer_ai_start', {
      location: 'bulk',
    });
    setIsGeneratingBulk(true);
    try {
      const generatedAnswerList = await mutateAsync({
        locale,
        question,
        answerList: answerList.map((a, index) => ({
          key: index,
          text: a.text,
        })),
        previousOutput: answerList.map((a, index) => ({
          key: index,
          text: a.text,
        })),
      });
      generatedAnswerList && handleGeneratedAnswerList(generatedAnswerList);
      const duration = (Date.now() - start) / 1000;
      logger.track('add_subjective_answer_ai_end', {
        location: 'bulk',
        duration_seconds: duration,
      });
    } catch (_) {
      const duration = (Date.now() - start) / 1000;
      logger.track('add_subjective_answer_ai_error', {
        location: 'bulk',
        duration_seconds: duration,
      });
      showAiErrorAlert();
    } finally {
      setIsGeneratingBulk(false);
    }
  }, [
    question,
    answerList,
    handleGeneratedAnswerList,
    mutateAsync,
    locale,
    showAiErrorAlert,
    setIsGeneratingBulk,
  ]);

  return {
    generate,
    disabled,
  };
};

export const useAiGenerateSubjectiveAnswerListMutation = () => {
  return useMutation<
    AnswerItem[],
    DefaultError,
    AiGenerateSubjectiveAnswerListQuery
  >({
    mutationFn: async query => {
      const res = await AI_HTTP_CLIENT.post(
        `/quiz-builder/generate/subjective-answer-list`,
        query,
      );
      return res.data;
    },
  });
};
